import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import PropTypes from 'prop-types';
import { formatDate } from '../../utils/date';
import apiService from '../../axios/apiService';
import { useUserLocale } from '../../hooks/useLocale';
import { useGetCase } from '../../hooks/cases/useGetCase';
import BasicCard from '../../components/core/Cards/BasicCard';
import BasicDivider from '../../components/core/Divider/BasicDivider';
import BreadCrumbs from '../../components/core/BreadCrumbs';
import InfoItem from '../../components/core/Cards/InfoItem';
import { useLocation, useNavigate } from 'react-router-dom';
import { BAD_REQUEST_STATUS_CODE } from '../../axios/constants';
import {
  Col,
  Form,
  notification,
  Row,
  Typography,
  Space,
  Button,
  Input,
  ConfigProvider,
  Flex,
  Modal,
} from 'antd';
import UploadFiles from './Forms/UploadFiles';
import { getSearchParamFromLocation } from '../../utils/mapper';
import {
  getPatientDetailInfoItems,
  getStudyPurposeInfoItems,
  mapAntFileToServerFile,
  mapCaseToRadiologistUpload,
  mapCaseToUpload,
} from '../../utils/case';
import { APP_TERMS } from '../../app-terms';
import FileTableList from '../../components/core/File/FileTable';
import TextEditor from '../../components/TextEditor';
import RadiologistFilesTableField from '../../components/Forms/buisness/RadiologistFilesTableField';
import CaseReportTemplateDownloader from '../../components/core/File/CaseReportTemplateDownloader';
import {
  CASE_REVIEW_STATUS_ASSIGNED,
  CASE_REVIEW_STATUS_SUMITTED,
  CASE_STATUS_SUBMITTED,
} from './CaseConstants';

const { Text } = Typography;
const { TextArea } = Input;
const ConfirmReportFilesConfig = {
  title: 'Confirm Reports',
  content: 'Are you sure you want to proceed with radiologist reports?',
  okText: 'Yes',
  cancelText: 'No',
};

const EditFormLayout = ({
  caseId,
  initialValues,
  loading,
  breadCrumbs,
  onFormSubmit,
  onMarkAsCompleted,
  onMarkAsAccepted,
  onMarkAsRejected,
}) => {
  const [modal, modalContextHolder] = Modal.useModal();
  const [api, contentHolder] = notification.useNotification();
  const [otherFieldsForm] = Form.useForm();
  const [uploadFilesForm] = Form.useForm();
  const [isUploading, setIsUploading] = useState(false);
  const buttonsDisabled = loading || isUploading;
  const navigate = useNavigate();
  const { userLocale } = useUserLocale();
  const patientDetails = useMemo(
    () => getPatientDetailInfoItems(initialValues, userLocale),
    [initialValues]
  );
  const studyPurpose = useMemo(
    () => getStudyPurposeInfoItems(initialValues),
    [initialValues]
  );
  const files = useMemo(() => initialValues?.files, [initialValues]);
  const caseStatus = useMemo(() => initialValues?.status, [initialValues]);
  const caseReviewStatus = useMemo(
    () => initialValues?.reviewStatus,
    [initialValues]
  );

  useEffect(() => {
    if (!initialValues) return;

    // Map Case details to input form values
    const initialUploads = mapCaseToUpload(initialValues);
    const initialRadiologistUploads =
      mapCaseToRadiologistUpload(initialValues)?.files;
    const internalComments = initialValues?.internalComments || '';
    const caseResultSummary = initialValues?.caseResultSummary || '';

    // initialize forms with case input initialValues
    initialUploads && uploadFilesForm.setFieldsValue(initialUploads);
    otherFieldsForm.setFieldsValue({
      internalComments,
      caseResultSummary,
      radiologistFiles: initialRadiologistUploads,
    });
  }, [initialValues]);

  const validateForm = async () => {
    try {
      await uploadFilesForm?.validateFields();
      await otherFieldsForm?.validateFields();
      return true;
    } catch (error) {
      console.error(
        'Errors : ',
        error?.errorFields?.flatMap((e) => e.errors)?.join('\n')
      );
      return false;
    }
  };

  const getUpdatedFormValues = () => ({
    cmsFiles: uploadFilesForm?.getFieldValue('files'),
    ...(otherFieldsForm?.getFieldsValue() || {}),
  });

  const handleSubmit = async () => {
    const isValid = await validateForm();
    isValid && onFormSubmit?.(getUpdatedFormValues());
  };

  const checkAndAlertMissingCMSFiles = async (formValues) => {
    if (!formValues?.cmsFiles?.length && !formValues?.radiologistFiles?.length) {
      // When completing a case there must be atleast one report. CMS files are considered as reports,
      // if same aren't available then radiologist files are used as reports.
      api.error({
        message: 'Report Missing',
        description: (
          <p>
            Please provide an <b>Admin Report!</b>
          </p>
        ),
      });
      return false;
    }
    if(!formValues?.cmsFiles?.length) {
      const confirmed = await modal.confirm(ConfirmReportFilesConfig);
      return confirmed;
    }
    return true;
  };

  const handleMarkAsCompleted = async () => {
    const isValid = await validateForm();
    const values = getUpdatedFormValues();
    if (!(await checkAndAlertMissingCMSFiles(values))) return;
    isValid && onMarkAsCompleted?.(values);
  };
  const handleMarkAsAccepted = async () => {
    const isValid = await validateForm();
    const values = getUpdatedFormValues();
    if (!(await checkAndAlertMissingCMSFiles(values))) return;
    isValid && onMarkAsAccepted?.(values);
  };
  const handleMarkAsDenied = async () => {
    const isValid = await validateForm();
    isValid && onMarkAsRejected?.(getUpdatedFormValues());
  };

  return (
    <>
      {contentHolder}
      <BreadCrumbs items={breadCrumbs} />
      <div className="row card">
        <div className="card invoices-tabs-card">
          <div className="card-body card-body pt-0 pb-0">
            <div className="invoices-main-tabs">
              <div className="row align-items-center">
                <div className="wizard mt-4">
                  <Row justify={'space-between'}>
                    <Col xs={24} sm={24} md={8} className="text-start">
                      <b>Case ID: </b>
                      {initialValues?.caseId}
                    </Col>
                    <Col xs={24} sm={24} md={8} className="text-center">
                      <b>Date: </b>
                      {initialValues?.createdAt &&
                        formatDate(
                          initialValues?.createdAt,
                          APP_TERMS.date_1_format,
                          userLocale
                        )}
                    </Col>
                    <Col xs={24} sm={24} md={8} className="text-end">
                      <b>Patient Name: </b>
                      {`${initialValues?.patient?.firstName} ${initialValues?.patient?.lastName}`}
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
            <Row gutter={[16, 16]} className="mt-3">
              <Col sm={24} md={12}>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={`${APP_TERMS.generic_patients} ${APP_TERMS.generic_details}`}
                >
                  {patientDetails?.map((details, index) => {
                    return (
                      <Fragment key={details?.label}>
                        <InfoItem
                          label={details?.label}
                          value={details?.value}
                        />
                        {index < patientDetails?.length - 1 ? (
                          <BasicDivider />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </BasicCard>
              </Col>
              <Col sm={24} md={12}>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={`${APP_TERMS.generic_study} ${APP_TERMS.generic_purpose}`}
                >
                  {studyPurpose?.map((details, index) => {
                    return (
                      <Fragment key={details?.label}>
                        <InfoItem
                          label={details?.label}
                          value={details?.value}
                          type={'vertical'}
                        />
                        {index < studyPurpose?.length - 1 ? (
                          <BasicDivider />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </BasicCard>
              </Col>
              <Col sm={24} md={12}>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={APP_TERMS.generic_files}
                >
                  <FileTableList
                    dataSource={files}
                    caseId={initialValues?.caseId}
                  />
                </BasicCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]} className="mt-3">
              <Col span={24}>
                <Flex justify="end" className="mb-2">
                  <CaseReportTemplateDownloader caseId={caseId} />
                </Flex>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={`${APP_TERMS.process_case_title}`}
                >
                  <UploadFiles
                    form={uploadFilesForm}
                    onUploadingChange={(uploading) => setIsUploading(uploading)}
                  />
                  <Form
                    layout="vertical"
                    name="otherFields"
                    form={otherFieldsForm}
                    initialValues={initialValues}
                  >
                    <Row gutter={[16, 16]} className="mt-3">
                      <Col span={24}>
                        <Form.Item
                          initialValue={''}
                          label={
                            <Text>{APP_TERMS.case_result_summary_title}</Text>
                          }
                          name="caseResultSummary"
                        >
                          <TextEditor />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={24}>
                        <Form.Item
                          shouldUpdate={true}
                          noStyle
                          name={'radiologistFiles'}
                        >
                          <RadiologistFilesTableField
                            caseId={initialValues?.caseId}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label={
                            <Text>
                              {APP_TERMS.generic_internal}{' '}
                              {APP_TERMS.generic_comments} -{' '}
                              <Text type="danger">Only for admin</Text>
                            </Text>
                          }
                          name="internalComments"
                        >
                          <TextArea
                            rows={4}
                            placeholder={''}
                            defaultValue={''}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </BasicCard>
              </Col>
            </Row>
            <Space className="mt-3">
              <Button
                disabled={buttonsDisabled}
                loading={loading === 'saving'}
                type="primary"
                htmlType="button"
                onClick={handleSubmit}
              >
                Save
              </Button>
              {modalContextHolder}
              {caseStatus === CASE_STATUS_SUBMITTED && caseReviewStatus !== CASE_REVIEW_STATUS_ASSIGNED ? (
                <ConfigProvider
                  theme={{
                    components: {
                      Button: {
                        defaultBg: 'rgb(70, 170, 26)',
                        defaultBorderColor: 'rgb(70, 170, 26)',
                        defaultColor: 'rgba(255,255,255,0.88)',
                        defaultHoverBg: 'rgb(94, 211, 35)',
                        defaultHoverBorderColor: 'rgb(94, 211, 35)',
                        defaultHoverColor: 'rgb(255,255,255)',
                      },
                    },
                  }}
                >
                  <Button
                    disabled={buttonsDisabled}
                    loading={loading === 'completing'}
                    type="default"
                    htmlType="button"
                    onClick={handleMarkAsCompleted}
                  >
                    Complete Case
                  </Button>
                </ConfigProvider>
              ) : null}
              {caseReviewStatus === CASE_REVIEW_STATUS_SUMITTED ? (
                <>
                  <ConfigProvider
                    theme={{
                      components: {
                        Button: {
                          defaultBg: 'rgb(82,196,26)',
                          defaultBorderColor: 'rgb(82,196,26)',
                          defaultColor: 'rgba(255,255,255,0.88)',
                          defaultHoverBg: 'rgb(107,228,47)',
                          defaultHoverBorderColor: 'rgb(107,228,47)',
                          defaultHoverColor: 'rgb(255,255,255)',
                        },
                      },
                    }}
                  >
                    <Button
                      disabled={buttonsDisabled}
                      loading={loading === 'accepting'}
                      type="default"
                      htmlType="button"
                      onClick={handleMarkAsAccepted}
                    >
                      Accept Review
                    </Button>
                  </ConfigProvider>
                  <Button
                    danger
                    disabled={buttonsDisabled}
                    loading={loading === 'denning'}
                    type="primary"
                    htmlType="button"
                    onClick={handleMarkAsDenied}
                  >
                    Deny Review
                  </Button>
                </>
              ) : null}
              <Button
                htmlType="button"
                disabled={buttonsDisabled}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </Space>
          </div>
        </div>
      </div>
    </>
  );
};

EditFormLayout.propTypes = {
  caseId: PropTypes.string,
  initialValues: PropTypes.any,
  loading: PropTypes.string,
  breadCrumbs: PropTypes.array,
  onFormSubmit: PropTypes.func,
  onMarkAsCompleted: PropTypes.func,
  onMarkAsAccepted: PropTypes.func,
  onMarkAsRejected: PropTypes.func,
};

const BREAD_CRUMBS = [
  { label: 'Case', to: '/user/case/created' },
  { label: 'Edit', to: '/user/case/edit' },
];
const EditCase = () => {
  const { data } = useGetCase();
  const [loading, setLoading] = useState();
  const [api, contentHolder] = notification.useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const caseId = useMemo(
    () => getSearchParamFromLocation('id', location),
    [location]
  );

  const handleSubmit = async (data, _loading = 'saving') => {
    const payload = {
      ...data,
      cmsFiles: data?.cmsFiles?.map(mapAntFileToServerFile),
      radiologistFiles: data?.radiologistFiles?.map(mapAntFileToServerFile),
    };
    setLoading(_loading);
    try {
      const id = getSearchParamFromLocation('id', location);
      const response = await apiService.updateCase(id, payload);
      api.success({
        message: 'Success',
        description: response?.message,
        showProgress: false,
      });
      navigate('/user/case/submitted');
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error?.response?.status === BAD_REQUEST_STATUS_CODE &&
        !error?.response?.data?.formErrorMessage
      )
        return;
      api.error({
        message: 'Error',
        description: error?.response?.data?.error || error?.message,
        showProgress: false,
      });
    } finally {
      setLoading(undefined);
    }
  };

  const handleMarkAsCompleted = async (data) => {
    return handleSubmit(
      {
        ...(data || {}),
        markAsCompleted: true,
      },
      'completing'
    );
  };
  const handleMarkAsAccepted = async (data) => {
    return handleSubmit(
      {
        ...(data || {}),
        markAsAccepted: true,
      },
      'accepting'
    );
  };
  const handleMarkAsRejected = async (data) => {
    return handleSubmit(
      {
        ...(data || {}),
        markAsRejected: true,
      },
      'denning'
    );
  };

  return (
    <>
      {contentHolder}
      <Form.Provider>
        <EditFormLayout
          caseId={caseId}
          loading={loading}
          initialValues={data}
          breadCrumbs={BREAD_CRUMBS}
          onFormSubmit={handleSubmit}
          onMarkAsCompleted={handleMarkAsCompleted}
          onMarkAsAccepted={handleMarkAsAccepted}
          onMarkAsRejected={handleMarkAsRejected}
        />
      </Form.Provider>
    </>
  );
};

export default EditCase;
